import React, {useMemo, useState, useEffect, RefObject} from 'react';
import {SVGPathData, SVGPathDataTransformer} from 'svg-pathdata';

export type MaskShape = {
    width?: number;
    height?: number;
    fluid?: boolean;
    path?: string;
    position: 'top' | 'middle' | 'bottom' | 'left' | 'right';
    flipX?: boolean;
    flipY?: boolean;
    inverted?: boolean;
    showStroke?: boolean;
};

const BASE_WIDTH = 1226; // Базовая ширина для масок

// Вспомогательная функция для создания матрицы трансформации
function createTransformMatrix(scaleX: number, scaleY: number, translateX: number = 0, translateY: number = 0) {
    return SVGPathDataTransformer.MATRIX(scaleX, 0, 0, scaleY, translateX, translateY);
}

// Функция для трансформации path с помощью svg-pathdata
function transformPath(pathString: string, scaleX: number, scaleY: number, translateX: number = 0, translateY: number = 0): string {
    const pathData = new SVGPathData(pathString);
    const transformed = pathData.transform(createTransformMatrix(scaleX, scaleY, translateX, translateY));
    return transformed.encode();
}

// Функция для создания прямоугольника как path
function createRectPath(x: number, y: number, width: number, height: number): string {
    return `M${x},${y} L${x + width},${y} L${x + width},${y + height} L${x},${y + height} Z`;
}

export function useMaskShapes(maskShapes: MaskShape[], maskId: string, ref?: RefObject<HTMLElement | null>, flatten?: boolean, strokeWidth?: number) {
    const [containerSize, setContainerSize] = useState({width: 0, height: 0});

    useEffect(() => {
        if (!ref?.current) return;

        const updateSize = () => {
            if (ref.current) {
                const rect = ref.current.getBoundingClientRect();
                setContainerSize({width: rect.width, height: rect.height});
            }
        };

        // Первоначальное измерение
        updateSize();

        // Наблюдение за изменениями размера
        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(ref.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [ref]);

    const maskIds = useMemo(() => {
        return maskShapes
            .map((shape, index) => shape.path ? `${maskId}-${index}` : null)
            .filter(Boolean) as string[];
    }, [maskShapes, maskId]);

    const svgJSX = useMemo(() => {
        if (!maskShapes.length) return null;

        // Если контейнер еще не измерен, используем базовую ширину
        const currentWidth = containerSize.width || BASE_WIDTH;
        const currentHeight = containerSize.height || 600;

        // Сортируем элементы по позиции: top, middle, bottom
        const sortedShapes = [...maskShapes].sort((a, b) => {
            const positionOrder = {top: 0, middle: 1, bottom: 2, left: 3, right: 4};
            return positionOrder[a.position] - positionOrder[b.position];
        });

        // Определяем базовую ширину для масштабирования
        // Для вертикальных макетов используем BASE_WIDTH, для остальных - ширину первого shape с path
        const firstShapeWithPath = sortedShapes.find(s => s.path && s.width);
        const baseWidth = firstShapeWithPath ? firstShapeWithPath.width : BASE_WIDTH;

        // Вычисляем коэффициент масштабирования
        const scale = currentWidth / baseWidth;

        // Создаем базовый SVG элемент для clipPath

        // Проверяем, есть ли инвертированные маски
        const hasInvertedMasks = sortedShapes.some(shape => shape.inverted);

        // Вычисляем высоты для top и bottom
        const topShape = sortedShapes.find(s => s.position === 'top');
        const bottomShape = sortedShapes.find(s => s.position === 'bottom');
        const topHeight = topShape ? (topShape.height || 0) * scale : 0;
        const bottomHeight = bottomShape ? (bottomShape.height || 0) * scale : 0;

        // Собираем все paths для clipPath
        let clipPathElements: string[] = [];

        // Если нужно объединить все в единый path
        if (flatten && !hasInvertedMasks) {
            // Определяем layout тип
            const leftShape = sortedShapes.find(s => s.position === 'left');
            const rightShape = sortedShapes.find(s => s.position === 'right');
            const isHorizontalLayout = leftShape || rightShape;

            let combinedCommands: any[] = [];

            sortedShapes.forEach(shape => {
                if (shape.position === 'middle' && shape.fluid) {
                    let rectPath: string;

                    if (isHorizontalLayout) {
                        // Горизонтальная раскладка для кнопок
                        const leftWidth = leftShape && leftShape.width ? (leftShape.height || 110) / 110 * currentHeight : 0;
                        const rightWidth = rightShape && rightShape.width ? (rightShape.height || 110) / 110 * currentHeight * (rightShape.width / (rightShape.height || 110)) : 0;
                        const middleWidth = Math.max(0, currentWidth - leftWidth - rightWidth);
                        const x = leftWidth;

                        rectPath = createRectPath(x, 0, middleWidth, currentHeight);
                    } else {
                        // Вертикальная раскладка
                        const extraHeight = 2;
                        const middleHeight = Math.max(0, currentHeight - topHeight - bottomHeight + extraHeight);
                        const topOverlap = 1;
                        const y = topHeight - topOverlap;

                        rectPath = createRectPath(0, y, currentWidth, middleHeight + topOverlap);
                    }

                    const pathData = new SVGPathData(rectPath);
                    combinedCommands = combinedCommands.concat(pathData.commands);

                } else if (shape.path) {
                    let transformedPath = shape.path;

                    if (shape.position === 'left' || shape.position === 'right') {
                        // Горизонтальные элементы (кнопки)
                        const originalHeight = shape.height || 110;
                        const scaleFactorY = currentHeight / originalHeight;
                        const scaleFactorX = scaleFactorY;

                        let translateX = 0;
                        if (shape.position === 'right') {
                            const scaledWidth = (shape.width || 0) * scaleFactorX;
                            translateX = currentWidth - scaledWidth;
                        }

                        transformedPath = transformPath(shape.path, scaleFactorX, scaleFactorY, translateX, 0);
                    } else if (shape.position === 'top' || shape.position === 'bottom') {
                        // Вертикальные элементы
                        const shapeBaseWidth = shape.width || BASE_WIDTH;
                        const scaleFactorX = currentWidth / shapeBaseWidth;
                        const scaleFactorY = scale;

                        const scaleX = shape.flipX ? -scaleFactorX : scaleFactorX;
                        let translateY = 0;

                        if (shape.position === 'bottom') {
                            translateY = currentHeight - (shape.height || 0) * scaleFactorY;
                        }

                        transformedPath = transformPath(shape.path, scaleX, scaleFactorY, 0, translateY);
                    } else {
                        // Остальные элементы
                        const scaleX = shape.flipX ? -scale : scale;
                        transformedPath = transformPath(shape.path, scaleX, scale, 0, 0);
                    }

                    // Flip по X через дополнительную трансформацию
                    if (shape.flipX && shape.width) {
                        transformedPath = transformPath(transformedPath, 1, 1, shape.width, 0);
                    }

                    const pathData = new SVGPathData(transformedPath);
                    combinedCommands = combinedCommands.concat(pathData.commands);
                }
            });

            // Создаем единый объединенный path
            if (combinedCommands.length > 0) {
                const finalPath = new SVGPathData(combinedCommands);
                clipPathElements.push(`<path d="${finalPath.encode()}"/>`);
            }

        } else if (hasInvertedMasks) {
            // Для инвертированных масок объединяем в один path с evenodd fill-rule
            let combinedCommands: any[] = [];

            // Добавляем внешний прямоугольник
            const outerRect = createRectPath(0, 0, currentWidth, currentHeight);
            const outerPathData = new SVGPathData(outerRect);
            combinedCommands = combinedCommands.concat(outerPathData.commands);

            sortedShapes.forEach(shape => {
                if (shape.position === 'middle' && shape.fluid) {
                    const extraHeight = 2;
                    const middleHeight = Math.max(0, currentHeight - topHeight - bottomHeight + extraHeight);
                    const topOverlap = 1;
                    const y = topHeight - topOverlap;

                    const rectPath = createRectPath(0, y, currentWidth, middleHeight + topOverlap);
                    const pathData = new SVGPathData(rectPath);
                    combinedCommands = combinedCommands.concat(pathData.commands);
                } else if (shape.path) {
                    const scaleX = shape.flipX ? -scale : scale;
                    let translateY = 0;

                    if (shape.position === 'bottom') {
                        translateY = currentHeight - (shape.height || 0) * scale;
                    }

                    let transformedPath = transformPath(shape.path, scaleX, scale, 0, translateY);

                    if (shape.flipX && shape.width) {
                        transformedPath = transformPath(transformedPath, 1, 1, shape.width, 0);
                    }

                    const pathData = new SVGPathData(transformedPath);
                    combinedCommands = combinedCommands.concat(pathData.commands);
                }
            });

            const finalPath = new SVGPathData(combinedCommands);
            clipPathElements.push(`<path d="${finalPath.encode()}" fill-rule="evenodd"/>`);
        } else {
            // Обычная логика - создаем отдельные элементы
            const leftShape = sortedShapes.find(s => s.position === 'left');
            const rightShape = sortedShapes.find(s => s.position === 'right');
            const isHorizontalLayout = leftShape || rightShape;

            sortedShapes.forEach(shape => {
                if (shape.position === 'middle' && shape.fluid) {
                    if (isHorizontalLayout) {
                        // Горизонтальная раскладка для кнопок
                        const leftWidth = leftShape && leftShape.width ? (leftShape.height || 110) / 110 * currentHeight : 0;
                        const rightWidth = rightShape && rightShape.width ? (rightShape.height || 110) / 110 * currentHeight * (rightShape.width / (rightShape.height || 110)) : 0;
                        const middleWidth = Math.max(0, currentWidth - leftWidth - rightWidth);

                        const rectPath = createRectPath(leftWidth, 0, middleWidth, currentHeight);
                        clipPathElements.push(`<path d="${rectPath}"/>`);
                    } else {
                        // Вертикальная раскладка
                        const extraHeight = 2;
                        const middleHeight = Math.max(0, currentHeight - topHeight - bottomHeight + extraHeight);
                        const topOverlap = 1;

                        const rectPath = createRectPath(0, topHeight - topOverlap, currentWidth, middleHeight + topOverlap);
                        clipPathElements.push(`<path d="${rectPath}"/>`);
                    }

                } else if (shape.path) {
                    let transformedPath = shape.path;

                    if (shape.position === 'left' || shape.position === 'right') {
                        // Горизонтальные элементы (кнопки)
                        const originalHeight = shape.height || 110;
                        const scaleFactorY = currentHeight / originalHeight;
                        const scaleFactorX = scaleFactorY;

                        let translateX = 0;
                        if (shape.position === 'right') {
                            const scaledWidth = (shape.width || 0) * scaleFactorX;
                            translateX = currentWidth - scaledWidth;
                        }

                        transformedPath = transformPath(shape.path, scaleFactorX, scaleFactorY, translateX, 0);
                    } else if (shape.position === 'top' || shape.position === 'bottom') {
                        // Вертикальные элементы
                        const shapeBaseWidth = shape.width || BASE_WIDTH;
                        const scaleFactorX = currentWidth / shapeBaseWidth;
                        const scaleFactorY = scale;

                        const scaleX = shape.flipX ? -scaleFactorX : scaleFactorX;
                        let translateY = 0;

                        if (shape.position === 'bottom') {
                            translateY = currentHeight - (shape.height || 0) * scaleFactorY;
                        }

                        transformedPath = transformPath(shape.path, scaleX, scaleFactorY, 0, translateY);
                    } else {
                        // Остальные элементы
                        const scaleX = shape.flipX ? -scale : scale;
                        transformedPath = transformPath(shape.path, scaleX, scale, 0, 0);
                    }

                    // Flip по X через дополнительную трансформацию
                    if (shape.flipX && shape.width) {
                        transformedPath = transformPath(transformedPath, 1, 1, shape.width, 0);
                    }

                    clipPathElements.push(`<path d="${transformedPath}"/>`);
                }
            });
        }

        // Создаем финальный SVG с clipPath
        const svgHTML = `
            <svg width="0" height="0" style="position: absolute;">
                <defs>
                    <clipPath id="${maskId}">
                        ${clipPathElements.join('')}
                    </clipPath>
                </defs>
            </svg>
        `;

        // Парсим и возвращаем как React элемент
        return (
            <div
                key={`${maskId}-${sortedShapes.length}`}
                style={{position: 'absolute', width: 0, height: 0}}
                dangerouslySetInnerHTML={{__html: svgHTML}}
            />
        );
    }, [maskShapes, maskId, containerSize.width, containerSize.height, flatten]);

    // Функция для создания standalone SVG элемента
    const getStandaloneSVG = useMemo(() => {
        if (!containerSize.width || !containerSize.height) return null;

        const currentWidth = containerSize.width;
        const currentHeight = containerSize.height;

        // Определяем базовую ширину для масштабирования
        const firstShapeWithPath = maskShapes.find(s => s.path && s.width);
        const baseWidth = firstShapeWithPath ? firstShapeWithPath.width : BASE_WIDTH;
        const scale = currentWidth / baseWidth;

        // Сортируем элементы
        const sortedShapes = [...maskShapes].sort((a, b) => {
            const positionOrder = {top: 0, middle: 1, bottom: 2, left: 3, right: 4};
            return positionOrder[a.position] - positionOrder[b.position];
        });

        // Вычисляем высоты
        const topShape = sortedShapes.find(s => s.position === 'top');
        const bottomShape = sortedShapes.find(s => s.position === 'bottom');
        const topHeight = topShape ? (topShape.height || 0) * scale : 0;
        const bottomHeight = bottomShape ? (bottomShape.height || 0) * scale : 0;

        // Определяем layout тип
        const leftShape = sortedShapes.find(s => s.position === 'left');
        const rightShape = sortedShapes.find(s => s.position === 'right');
        const isHorizontalLayout = leftShape || rightShape;

        let svgElements: string[] = [];

        sortedShapes.forEach(shape => {
            if (shape.position === 'middle' && shape.fluid) {
                if (isHorizontalLayout) {
                    const leftWidth = leftShape && leftShape.width ? (leftShape.height || 110) / 110 * currentHeight : 0;
                    const rightWidth = rightShape && rightShape.width ? (rightShape.height || 110) / 110 * currentHeight * (rightShape.width / (rightShape.height || 110)) : 0;
                    const middleWidth = Math.max(0, currentWidth - leftWidth - rightWidth);

                    const rectPath = createRectPath(leftWidth, 0, middleWidth, currentHeight);
                    svgElements.push(`<path d="${rectPath}" fill="currentColor"/>`);
                } else {
                    const extraHeight = 2;
                    const middleHeight = Math.max(0, currentHeight - topHeight - bottomHeight + extraHeight);
                    const topOverlap = 1;

                    const rectPath = createRectPath(0, topHeight - topOverlap, currentWidth, middleHeight + topOverlap);
                    svgElements.push(`<path d="${rectPath}" fill="currentColor"/>`);
                }

            } else if (shape.path) {
                let transformedPath = shape.path;

                if (shape.position === 'left' || shape.position === 'right') {
                    const originalHeight = shape.height || 110;
                    const scaleFactorY = currentHeight / originalHeight;
                    const scaleFactorX = scaleFactorY;

                    let translateX = 0;
                    if (shape.position === 'right') {
                        const scaledWidth = (shape.width || 0) * scaleFactorX;
                        translateX = currentWidth - scaledWidth;
                    }

                    transformedPath = transformPath(shape.path, scaleFactorX, scaleFactorY, translateX, 0);
                } else if (shape.position === 'top' || shape.position === 'bottom') {
                    const shapeBaseWidth = shape.width || BASE_WIDTH;
                    const scaleFactorX = currentWidth / shapeBaseWidth;
                    const scaleFactorY = scale;

                    const scaleX = shape.flipX ? -scaleFactorX : scaleFactorX;
                    let translateY = 0;

                    if (shape.position === 'bottom') {
                        translateY = currentHeight - (shape.height || 0) * scaleFactorY;
                    }

                    transformedPath = transformPath(shape.path, scaleX, scaleFactorY, 0, translateY);
                } else {
                    const scaleX = shape.flipX ? -scale : scale;
                    transformedPath = transformPath(shape.path, scaleX, scale, 0, 0);
                }

                if (shape.flipX && shape.width) {
                    transformedPath = transformPath(transformedPath, 1, 1, shape.width, 0);
                }

                svgElements.push(`<path d="${transformedPath}" fill="currentColor"/>`);
            }
        });

        // Создаем полноценный SVG элемент
        const standaloneSVG = `
            <svg width="${currentWidth}" height="${currentHeight}" viewBox="0 0 ${currentWidth} ${currentHeight}" xmlns="http://www.w3.org/2000/svg">
                ${svgElements.join('')}
            </svg>
        `;

        return standaloneSVG.trim();
    }, [maskShapes, containerSize.width, containerSize.height]);

    // Функция для получения объединенного path строки (для экспорта)
    const getFlattenedPath = useMemo(() => {
        if (!flatten || !containerSize.width || !containerSize.height) return null;

        const currentWidth = containerSize.width;
        const currentHeight = containerSize.height;

        // Определяем базовую ширину для масштабирования
        const firstShapeWithPath = maskShapes.find(s => s.path && s.width);
        const baseWidth = firstShapeWithPath ? firstShapeWithPath.width : BASE_WIDTH;
        const scale = currentWidth / baseWidth;

        // Сортируем элементы
        const sortedShapes = [...maskShapes].sort((a, b) => {
            const positionOrder = {top: 0, middle: 1, bottom: 2, left: 3, right: 4};
            return positionOrder[a.position] - positionOrder[b.position];
        });

        // Вычисляем высоты
        const topShape = sortedShapes.find(s => s.position === 'top');
        const bottomShape = sortedShapes.find(s => s.position === 'bottom');
        const topHeight = topShape ? (topShape.height || 0) * scale : 0;
        const bottomHeight = bottomShape ? (bottomShape.height || 0) * scale : 0;

        // Определяем layout тип
        const leftShape = sortedShapes.find(s => s.position === 'left');
        const rightShape = sortedShapes.find(s => s.position === 'right');
        const isHorizontalLayout = leftShape || rightShape;

        let combinedCommands: any[] = [];

        sortedShapes.forEach(shape => {
            if (shape.position === 'middle' && shape.fluid) {
                let rectPath: string;

                if (isHorizontalLayout) {
                    const leftWidth = leftShape && leftShape.width ? (leftShape.height || 110) / 110 * currentHeight : 0;
                    const rightWidth = rightShape && rightShape.width ? (rightShape.height || 110) / 110 * currentHeight * (rightShape.width / (rightShape.height || 110)) : 0;
                    const middleWidth = Math.max(0, currentWidth - leftWidth - rightWidth);
                    const x = leftWidth;

                    rectPath = createRectPath(x, 0, middleWidth, currentHeight);
                } else {
                    const extraHeight = 2;
                    const middleHeight = Math.max(0, currentHeight - topHeight - bottomHeight + extraHeight);
                    const topOverlap = 1;
                    const y = topHeight - topOverlap;

                    rectPath = createRectPath(0, y, currentWidth, middleHeight + topOverlap);
                }

                const pathData = new SVGPathData(rectPath);
                combinedCommands = combinedCommands.concat(pathData.commands);

            } else if (shape.path) {
                let transformedPath = shape.path;

                if (shape.position === 'left' || shape.position === 'right') {
                    const originalHeight = shape.height || 110;
                    const scaleFactorY = currentHeight / originalHeight;
                    const scaleFactorX = scaleFactorY;

                    let translateX = 0;
                    if (shape.position === 'right') {
                        const scaledWidth = (shape.width || 0) * scaleFactorX;
                        translateX = currentWidth - scaledWidth;
                    }

                    transformedPath = transformPath(shape.path, scaleFactorX, scaleFactorY, translateX, 0);
                } else if (shape.position === 'top' || shape.position === 'bottom') {
                    const shapeBaseWidth = shape.width || BASE_WIDTH;
                    const scaleFactorX = currentWidth / shapeBaseWidth;
                    const scaleFactorY = scale;

                    const scaleX = shape.flipX ? -scaleFactorX : scaleFactorX;
                    let translateY = 0;

                    if (shape.position === 'bottom') {
                        translateY = currentHeight - (shape.height || 0) * scaleFactorY;
                    }

                    transformedPath = transformPath(shape.path, scaleX, scaleFactorY, 0, translateY);
                } else {
                    const scaleX = shape.flipX ? -scale : scale;
                    transformedPath = transformPath(shape.path, scaleX, scale, 0, 0);
                }

                if (shape.flipX && shape.width) {
                    transformedPath = transformPath(transformedPath, 1, 1, shape.width, 0);
                }

                const pathData = new SVGPathData(transformedPath);
                combinedCommands = combinedCommands.concat(pathData.commands);
            }
        });

        // Создаем финальный объединенный path
        if (combinedCommands.length > 0) {
            const finalPath = new SVGPathData(combinedCommands);
            return finalPath.encode();
        }

        return null;
    }, [maskShapes, containerSize.width, containerSize.height, flatten]);

    // Функция для создания объединенного standalone SVG (только при flatten=true)
    const getFlattenedSVG = useMemo(() => {
        if (!flatten || !containerSize.width || !containerSize.height) return null;

        const flattenedPath = getFlattenedPath;
        if (!flattenedPath) return null;

        const currentWidth = containerSize.width;
        const currentHeight = containerSize.height;

        return `<svg width="${currentWidth}" height="${currentHeight}" viewBox="0 0 ${currentWidth} ${currentHeight}" xmlns="http://www.w3.org/2000/svg">
    <path d="${flattenedPath}" fill="currentColor"/>
</svg>`;
    }, [getFlattenedPath, containerSize.width, containerSize.height, flatten]);

    // Функция для создания SVG с обводкой (stroke) - всегда использует объединенный path
    const getStrokeSVG = useMemo(() => {
        if (!strokeWidth || !containerSize.width || !containerSize.height) return null;

        const currentWidth = containerSize.width;
        const currentHeight = containerSize.height;

        // Определяем базовую ширину для масштабирования
        const firstShapeWithPath = maskShapes.find(s => s.path && s.width);
        const baseWidth = firstShapeWithPath ? firstShapeWithPath.width : BASE_WIDTH;
        const scale = currentWidth / baseWidth;

        // Сортируем элементы
        const sortedShapes = [...maskShapes].sort((a, b) => {
            const positionOrder = {top: 0, middle: 1, bottom: 2, left: 3, right: 4};
            return positionOrder[a.position] - positionOrder[b.position];
        });

        // Вычисляем высоты
        const topShape = sortedShapes.find(s => s.position === 'top');
        const bottomShape = sortedShapes.find(s => s.position === 'bottom');
        const topHeight = topShape ? (topShape.height || 0) * scale : 0;
        const bottomHeight = bottomShape ? (bottomShape.height || 0) * scale : 0;

        // Определяем layout тип
        const leftShape = sortedShapes.find(s => s.position === 'left');
        const rightShape = sortedShapes.find(s => s.position === 'right');
        const isHorizontalLayout = leftShape || rightShape;

        let combinedCommands: any[] = [];

        // Строим объединенный path
        sortedShapes.forEach(shape => {
            if (shape.position === 'middle' && shape.fluid) {
                let rectPath: string;

                if (isHorizontalLayout) {
                    const leftWidth = leftShape && leftShape.width ? (leftShape.height || 110) / 110 * currentHeight : 0;
                    const rightWidth = rightShape && rightShape.width ? (rightShape.height || 110) / 110 * currentHeight * (rightShape.width / (rightShape.height || 110)) : 0;
                    const middleWidth = Math.max(0, currentWidth - leftWidth - rightWidth);
                    const x = leftWidth;

                    rectPath = createRectPath(x, 0, middleWidth, currentHeight);
                } else {
                    const extraHeight = 2;
                    const middleHeight = Math.max(0, currentHeight - topHeight - bottomHeight + extraHeight);
                    const topOverlap = 1;
                    const y = topHeight - topOverlap;

                    rectPath = createRectPath(0, y, currentWidth, middleHeight + topOverlap);
                }

                const pathData = new SVGPathData(rectPath);
                combinedCommands = combinedCommands.concat(pathData.commands);

            } else if (shape.path) {
                let transformedPath = shape.path;

                if (shape.position === 'left' || shape.position === 'right') {
                    const originalHeight = shape.height || 110;
                    const scaleFactorY = currentHeight / originalHeight;
                    const scaleFactorX = scaleFactorY;

                    let translateX = 0;
                    if (shape.position === 'right') {
                        const scaledWidth = (shape.width || 0) * scaleFactorX;
                        translateX = currentWidth - scaledWidth;
                    }

                    transformedPath = transformPath(shape.path, scaleFactorX, scaleFactorY, translateX, 0);
                } else if (shape.position === 'top' || shape.position === 'bottom') {
                    const shapeBaseWidth = shape.width || BASE_WIDTH;
                    const scaleFactorX = currentWidth / shapeBaseWidth;
                    const scaleFactorY = scale;

                    const scaleX = shape.flipX ? -scaleFactorX : scaleFactorX;
                    let translateY = 0;

                    if (shape.position === 'bottom') {
                        translateY = currentHeight - (shape.height || 0) * scaleFactorY;
                    }

                    transformedPath = transformPath(shape.path, scaleX, scaleFactorY, 0, translateY);
                } else {
                    const scaleX = shape.flipX ? -scale : scale;
                    transformedPath = transformPath(shape.path, scaleX, scale, 0, 0);
                }

                if (shape.flipX && shape.width) {
                    transformedPath = transformPath(transformedPath, 1, 1, shape.width, 0);
                }

                const pathData = new SVGPathData(transformedPath);
                combinedCommands = combinedCommands.concat(pathData.commands);
            }
        });

        // Создаем финальный объединенный path
        if (combinedCommands.length > 0) {
            const finalPath = new SVGPathData(combinedCommands);
            const strokePath = finalPath.encode();

            return `<svg width="${currentWidth}" height="${currentHeight}" viewBox="0 0 ${currentWidth} ${currentHeight}" xmlns="http://www.w3.org/2000/svg">
    <defs>
        <filter id="outline-${maskId}" x="-50%" y="-50%" width="200%" height="200%">
            <!-- Эрозия с корректировкой для сохранения радиусов углов -->
            <feMorphology operator="erode" radius="${strokeWidth * 0.6}" result="eroded"/>
            <feComposite operator="out" in="SourceGraphic" in2="eroded"/>
        </filter>
    </defs>
    <path d="${strokePath}" 
          fill="currentColor" 
          filter="url(#outline-${maskId})"/>
</svg>`;
        }

        return null;
    }, [maskShapes, containerSize.width, containerSize.height, strokeWidth, maskId]);

    return {
        svgJSX,
        maskIds,
        clipPath: `url(#${maskId})`,
        containerWidth: containerSize.width,
        containerHeight: containerSize.height,
        flattenedPath: getFlattenedPath,
        standaloneSVG: getStandaloneSVG,
        flattenedSVG: getFlattenedSVG,
        strokeSVG: getStrokeSVG
    };
}
